import {Box} from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {ChangeEvent, FC, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {transactionsApi} from 'src/api';
import {Table} from 'src/components/tables/Table';
import {columnConfigTransactions} from 'src/components/transactions/TransactionsTable/TransactionsTable.tableConfig';
import {DatesType} from 'src/pages/Reports/Reports.types';
import {EmptyTable} from '../../tables/EmptyTable';
import {TransactionSearchRequestData} from 'src/types';
import {ChargerTransactionCard} from './ChargerTransactionCard';
import {TableLayout} from '../../tables/TableLayout';
import {ResponsiveTable} from '../../common/ResponsiveTable';
import {CardList} from 'src/components/common/CardList';
import {DateBasisToggle} from 'src/components/common/DateBasisToggle';
import {FilterBar} from 'src/components/common/FilterBar';
import {periodFieldSx} from 'src/components/common/FilterBar/FilterBar.sx';
import {DateSelect} from '../../common/DateSelect';
import {ChargerTransactionsTableType} from './ChargerTransactionsTable.types';
import {DateFilterType} from '../../common/TimeSelect/TimeSelect.types';

dayjs.extend(utc);

export const ChargerTransactionsTable: FC<ChargerTransactionsTableType> = ({
                                                                               businessId,
                                                                               chargerId,
                                                                           }) => {
    const intl = useIntl();

    const title = intl.formatMessage({id: 'transactions'});

    const [perPage, setPerPage] = useState(100);
    const [currentPage, setCurrentPage] = useState(0);
    const [getTransactions, transactions] = transactionsApi.useLazyGetTransactionsQuery();

    const [dates, setDates] = useState<DatesType>({
        allSessions: {
            from: dayjs.utc().startOf('year').valueOf(),
            to: dayjs.utc().valueOf(),
        },
    });
    const [dateFilterType, setDateFilterType] = useState<DateFilterType>('start');

    const handlePerPageChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const val = +e.target.value;
        if (typeof val === 'number') {
            setPerPage(val);
            setCurrentPage(0);
        }
    };

    const handleChangePage = (e: number) => {
        setCurrentPage(e);
    };

    /** Changing the basis changes which sessions fall in the period, so the paging starts over. */
    const handleDateFilterTypeChange = (type: DateFilterType) => {
        setDateFilterType(type);
        setCurrentPage(0);
    };

    const getData = async () => {
        const data: TransactionSearchRequestData = {
            businessId,
            chargerId,
            ...(dateFilterType === 'stop'
                ? {stopFrom: dates.allSessions.from, stopTo: dates.allSessions.to}
                : {startFrom: dates.allSessions.from, startTo: dates.allSessions.to}),
        };

        // Nothing derived from the account. Transaction search is one of the bodies the server
        // clamps: it writes the caller's own userId, employeeId or companyId onto the request
        // according to their reach, so naming them here changed nothing and read as though the
        // client were the one deciding.

        await getTransactions({
            data,
            params: {size: perPage, page: currentPage},
        });
    };

    useEffect(() => {
        void getData();
    }, [dates, perPage, currentPage, dateFilterType]);

    const handleChangeDates = (newFrom: number, newTo: number, field: string) => {
        return () => {
            setDates({
                [field]: {
                    from: newFrom,
                    to: newTo,
                },
            });
        };
    };

    return (
        <TableLayout
            toolbar={(
                /* The period and its basis sit on the title's own line. The filter band below a
                   heading is for a list with filters to put in it; this one has none, so the band
                   was a row of grey holding a date box and nothing else. */
                <FilterBar
                    title={title}
                    actions={(
                        <>
                            <Box sx={periodFieldSx}>
                                <DateSelect
                                    handleTimeChange={handleChangeDates}
                                    field={Object.keys(dates)[0]}
                                    dense
                                />
                            </Box>
                            <DateBasisToggle
                                value={dateFilterType}
                                onChange={handleDateFilterTypeChange}
                            />
                        </>
                    )}
                />
            )}
            totalPages={transactions?.data?.page?.totalPages ?? 0}
            page={currentPage}
            pageSize={perPage}
            onPageChange={handleChangePage}
            onPageSizeChange={handlePerPageChange}
        >
            <ResponsiveTable
                desktopElement={
                    <Table
                        sx={{
                            border: 'none',
                        }}
                        rows={transactions.data?.content ?? []}
                        hideFooter
                        columns={columnConfigTransactions(businessId)}
                    />
                }
                responsiveElement={
                    <>
                        <CardList>
                            {transactions?.data?.content?.map((item) => (
                                <ChargerTransactionCard
                                    key={item?.id}
                                    {...item}
                                />
                            ))}
                        </CardList>
                        {!transactions?.data?.content?.length && <EmptyTable/>}
                    </>
                }
            />
        </TableLayout>
    );
};
